import type {
  AdapterDeps,
  ConnectionWithAuth,
  ServiceAuthAdapter,
  ServiceAuthFactory,
  ServiceKey,
} from "./auth/types";
import type { StudioModule, StudioModuleConnector } from "./studio/types";
import { engageAuthFactory } from "../integrations/engage/auth";
import { myqueueAuthFactory, myqueueTasksAuthFactory } from "../integrations/myqueue/auth";
import { logocloudAuthFactory } from "../integrations/logocloud/auth";
import { transcribeAuthFactory } from "../integrations/transcribe/auth";

/**
 * Static factory map keyed by the *normalised* connector systemName (the
 * backend ships kebab-case with a `-connection` suffix, e.g.
 * `engage-workflow-connection`; `normaliseSystemName` strips that suffix
 * before lookup so the FACTORIES map only needs the base names). Aliases
 * map every distinct backend connector to the right Cognito adapter — the
 * adapter `key` it returns is the canonical ServiceKey used by integration
 * hooks (`useEngageClient`, `useMyQueueClient` …).
 */
const FACTORIES: Record<string, ServiceAuthFactory> = {
  engage: engageAuthFactory,
  "engage-workflow": engageAuthFactory,
  myqueue: myqueueAuthFactory,
  "myqueue-tasks": myqueueTasksAuthFactory,
 "engage-myqueue": myqueueTasksAuthFactory,
  logocloud: logocloudAuthFactory,
  transcribe: transcribeAuthFactory,
  "transcribe-qc": transcribeAuthFactory,
  // AIVA-6071: Deckorate now host-direct; uncomment to revert to connector.
  // deckorate: deckorateAuthFactory,
  // "deckorate-image-ai": deckorateAuthFactory,
  // DocuParser has no per-connector Cognito login/logout of its own — like
  // Deckorate, it authenticates host-direct with the add-in's own AIVA
  // Azure AD session (see integrations/docuparser/hooks/useDocuparserHttp.ts).
};

/**
 * The Studio backend ships the OAuth helper as a paired `{name}-connection`
 * systemName (e.g. `engage-workflow-connection`). Strip the suffix and
 * lowercase so FACTORIES / registry lookups land on the base connector.
 */
function normaliseSystemName(systemName: string): string {
  return systemName.toLowerCase().replace(/-connection$/, "");
}

export interface ConnectionRegistry {
  get(key: ServiceKey): ServiceAuthAdapter | undefined;
  list(): ServiceAuthAdapter[];
  has(key: ServiceKey): boolean;
  getConnection(key: ServiceKey): ConnectionWithAuth | undefined;
  dispose(): void;
}

interface RegistryWarnings {
  unknownAddinKeys: string[];
  missingAuthConfig: string[];
}

export interface BuildResult {
  registry: ConnectionRegistry;
  warnings: RegistryWarnings;
}

interface BuildOptions {
  factories?: Record<string, ServiceAuthFactory>;
  log?: (msg: string) => void;
}

/**
 * Builds a ConnectionRegistry from Studio's `modules[*].connectors[*]`. Each
 * unique systemName with a matching factory AND a non-empty authConfig
 * becomes one instantiated adapter; everything else is logged and skipped.
 *
 * Iteration source is modules (not the flat connections[]) so credentials
 * are read from where enrichModulesWithCredentials bound them. A connector
 * shared across modules carries the same merged authConfig on every
 * occurrence, so the first one wins for adapter instantiation.
 */
export function buildConnectionRegistry(
  modules: StudioModule[],
  deps: AdapterDeps,
  opts: BuildOptions = {},
): BuildResult {
  const factories = opts.factories ?? FACTORIES;
  const log = opts.log ?? ((m: string) => console.warn(m));
  const adapters = new Map<string, ServiceAuthAdapter>();
  const connectionsByKey = new Map<string, ConnectionWithAuth>();
  const warnings: RegistryWarnings = { unknownAddinKeys: [], missingAuthConfig: [] };
  const seen = new Set<string>();

  for (const module of modules) {
    for (const connector of module.connectors) {
      if (seen.has(connector.systemName)) continue;
      seen.add(connector.systemName);

      // Look up by the *normalised* systemName so the backend's
      // `engage-workflow-connection` resolves to the `engage-workflow`
      // factory entry without each backend variant needing its own key.
      const normalised = normaliseSystemName(connector.systemName);
      const factory = factories[connector.systemName] ?? factories[normalised];
      if (!factory) {
        warnings.unknownAddinKeys.push(connector.systemName);
        log(
          `[ConnectionRegistry] no adapter factory for systemName="${connector.systemName}"`,
        );
        continue;
      }
      if (!connector.authConfig) {
        warnings.missingAuthConfig.push(connector.systemName);
        log(
          `[ConnectionRegistry] connector "${connector.systemName}" missing authConfig`,
        );
        continue;
      }
      const adapter = factory.create(connector.authConfig, deps);
      const connection = synthesizeConnection(connector);
      // Index under every key Settings / integration hooks might use:
      //   adapter.key  — canonical ServiceKey (`engage`, `myqueue` …)
      //   raw          — what `modules[*].connectors[*].systemName` ships
      //   normalised   — same with `-connection` stripped
      // All three resolve to the same adapter instance; `list()` dedupes.
      const keys = new Set([adapter.key, connector.systemName, normalised]);
      for (const k of keys) {
        adapters.set(k, adapter);
        connectionsByKey.set(k, connection);
      }
    }
  }

  const registry: ConnectionRegistry = {
    get(key) {
      return adapters.get(key);
    },
    has(key) {
      return adapters.has(key);
    },
    list() {
      return Array.from(new Set(adapters.values()));
    },
    getConnection(key) {
      return connectionsByKey.get(key);
    },
    dispose() {
      // Dropping the registry is a LIFECYCLE concern — the provider rebuilds it
      // whenever `modules`/`deps` change identity (e.g. the AppConfigProvider
      // focus/visibility silent re-check hands down a fresh `modules` array),
      // on remount, and in dev on React StrictMode's mount→unmount→remount. It
      // is NOT a user sign-out. Calling adapter.disconnect() here would run
      // storage.clear(), wiping the persisted refresh token from localStorage
      // and silently signing the user out of every connector right after they
      // logged in. Only drop the in-memory lookup maps; the discarded adapters
      // are GC'd with their in-memory tokens, while the persisted refresh token
      // survives so the rebuilt registry can silentConnect() the session back.
      adapters.clear();
      connectionsByKey.clear();
    },
  };

  return { registry, warnings };
}

/**
 * Adapts a module's connector entry into the ConnectionWithAuth shape that
 * existing call sites (e.g. useDeckorateClient → getConnection().apiBaseUrl)
 * still expect.
 */
function synthesizeConnection(
  connector: StudioModuleConnector,
): ConnectionWithAuth {
  const conn: ConnectionWithAuth = {
    id: `conn-${connector.systemName}`,
    name: connector.name,
    status: "active",
    health: "healthy",
    addinKey: connector.systemName,
    category: "",
    lastHealthCheckAt: null,
    moduleLinks: [],
  };
  if (connector.authConfig) conn.authConfig = connector.authConfig;
  if (connector.apiBaseUrl) conn.apiBaseUrl = connector.apiBaseUrl;
  return conn;
}

/** Exported for tests that want to inject custom factories. */
export const DEFAULT_FACTORIES = FACTORIES;
